import { commissionImpact } from '../data';

export default function CommissionImpactSection() {
  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Section header */}
        <div className="max-w-[640px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            The same commission hits different traders differently
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            A $7 round-turn commission means nothing to a swing trader holding for weeks — but it is everything to a scalper who opens 15 positions a day.
          </p>
        </div>

        {/* Unified comparison card */}
        <div className="relative bg-foreground-950 rounded-card overflow-hidden">
          {/* Decorative glows */}
          <div className="absolute top-0 right-0 w-[350px] h-[350px] bg-accent-500/6 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[250px] h-[250px] bg-primary-500/4 rounded-full blur-[100px] pointer-events-none" />

          <div className="relative p-6 md:p-10 divide-y divide-foreground-800">
            {commissionImpact.map((item, idx) => {
              const monthlyTrades = item.tradesPerDay * item.daysPerMonth;
              const monthlyCommission = monthlyTrades * item.lotsPerTrade * item.commissionPerSide * 2;
              const spreadPerTrade = item.spreadPips * 10 * item.lotsPerTrade;
              const monthlySpread = monthlyTrades * spreadPerTrade;
              const monthlyTotal = monthlyCommission + monthlySpread;
              const commissionPct = monthlyTotal > 0 ? Math.round((monthlyCommission / monthlyTotal) * 100) : 0;
              const annualTotal = monthlyTotal * 12;

              const isHigh = commissionPct >= 60;
              const isMid = commissionPct >= 25 && commissionPct < 60;

              const barCommissionColor = isHigh ? 'bg-accent-500' : isMid ? 'bg-primary-500' : 'bg-secondary-500';
              const barSpreadColor = isHigh ? 'bg-accent-200' : isMid ? 'bg-primary-200' : 'bg-secondary-200';
              const dotColor = isHigh ? 'bg-accent-500' : isMid ? 'bg-primary-500' : 'bg-secondary-500';
              const tagColor = isHigh ? 'text-accent-400' : isMid ? 'text-primary-400' : 'text-secondary-400';
              const impactBg = isHigh ? 'bg-accent-500/10 text-accent-400 border-accent-500/20' : isMid ? 'bg-primary-500/10 text-primary-400 border-primary-500/20' : 'bg-secondary-500/10 text-secondary-400 border-secondary-500/20';

              return (
                <div key={item.trader} className="py-7 md:py-9 first:pt-4 last:pb-4">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-10">
                    {/* Left: Trader identity + bar visualization */}
                    <div className="flex-1 min-w-0">
                      {/* Trader header row */}
                      <div className="flex items-baseline gap-3 mb-5">
                        <span className="text-[22px] md:text-[26px] font-[750] text-background-50 tracking-tight leading-none">
                          {item.trader}
                        </span>
                        <span className={`text-[12px] font-semibold text-foreground-400`}>
                          {item.tradesPerDay} trades/day · {item.lotsPerTrade} lots · {monthlyTrades}/month
                        </span>
                      </div>

                      {/* BIG ratio bar */}
                      <div className="mb-3">
                        <div className="w-full h-10 bg-foreground-800 rounded-md overflow-hidden flex">
                          <div
                            className={`h-full ${barCommissionColor} transition-all duration-1000 ease-out flex items-center min-w-0`}
                            style={{ width: `${Math.max(commissionPct, 3)}%` }}
                          >
                            {commissionPct >= 14 && (
                              <span className="text-[12px] font-bold text-background-50 ml-3 whitespace-nowrap">
                                Commission {commissionPct}%
                              </span>
                            )}
                          </div>
                          <div
                            className={`h-full ${barSpreadColor} transition-all duration-1000 ease-out flex items-center justify-end min-w-0`}
                            style={{ width: `${Math.max(100 - commissionPct, 3)}%` }}
                          >
                            {100 - commissionPct >= 14 && (
                              <span className="text-[12px] font-bold text-foreground-700 mr-3 whitespace-nowrap">
                                Spread {100 - commissionPct}%
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Small labels under bar */}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-sm ${barCommissionColor}`} />
                            <span className="text-[11px] text-foreground-500">${monthlyCommission.toFixed(0)}/mo</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-sm ${barSpreadColor}`} />
                            <span className="text-[11px] text-foreground-500">${monthlySpread.toFixed(0)}/mo</span>
                          </div>
                        </div>
                      </div>

                      {/* Tagline */}
                      <p className="text-[14px] leading-relaxed text-foreground-300 mt-3">
                        {item.tagline}
                      </p>
                    </div>

                    {/* Right: Numbers + impact */}
                    <div className="lg:w-[220px] lg:flex-shrink-0 flex lg:flex-col items-start lg:items-end gap-4 lg:gap-4">
                      {/* Cost numbers */}
                      <div className="text-right">
                        <div className="text-[12px] font-semibold text-foreground-500 uppercase tracking-wider mb-1">
                          Monthly Cost
                        </div>
                        <div className="text-[36px] md:text-[42px] font-[800] leading-none text-background-50 tracking-tight">
                          ${monthlyTotal.toFixed(0)}
                        </div>
                        <div className="text-[13px] text-foreground-500 mt-1">
                          ${annualTotal.toFixed(0)} / year
                        </div>
                      </div>

                      {/* Impact badge */}
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border whitespace-nowrap ${impactBg}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                        {isHigh ? 'High Impact' : isMid ? 'Moderate' : 'Low Impact'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}